/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRPrompt,
  SLRPromptLabels,
  SLRPromptProps,
  SLRSpinner,
  showErrorToast,
  useDeleteMediaItem,
  useGetOrganizationMembershipInformation,
} from "@slr/shared-library";
import { ErrorView } from "components";
import {
  ENCRYPTION_ENABLED,
  PROFILE_ORGANIZATIONS_PATH,
  SHOW_ORGANIZATION_EDIT_PARAM,
} from "configs";
import {
  useDeleteOrganization,
  useGetOrganization,
  useGetOrganizationDeletionEligibility,
  useLeaveOrganization,
} from "hooks";
import { useQueryParam } from "providers";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useSessionStorage } from "usehooks-ts";
import {
  InvitationsList,
  MembersList,
  OrganizationDescription,
  OrganizationHeader,
} from ".";
import { KeyManagement, OrganizationModal } from "..";

export const OrganizationDetails: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationsPage.details",
  });

  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { solutionBarQuery, removeQueryParamsFromURL } = useQueryParam();
  const { showEncryption, showOrgEdit } = solutionBarQuery || {};

  const [hasKeyManagementModalAutoOpened, setHasKeyManagementModalAutoOpened] =
    useSessionStorage<boolean>("hasEncryptionModalAutoOpened", false);

  const {
    data: organization,
    isError,
    isSuccess: hasOrganizationLoaded,
  } = useGetOrganization(organizationId ?? "");

  const { isAdmin, isMember } =
    useGetOrganizationMembershipInformation(organizationId);

  const { mutate: leaveOrganization } = useLeaveOrganization();
  const { mutate: deleteOrganization } = useDeleteOrganization();
  const { mutate: deleteLogo } = useDeleteMediaItem(() =>
    showErrorToast("toasts.deleteMediaItem.error")
  );
  const { refetch: refetchDeletionEligibility } =
    useGetOrganizationDeletionEligibility(organizationId, isAdmin);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showKeyManagement, setShowKeyManagement] = useState(false);
  const [SLRPromptData, setSLRPromptData] = useState<SLRPromptProps | null>(
    null
  );
  const hideSLRPrompt = () => setSLRPromptData(null);

  const handleKeyManagementClose = useCallback(
    () => setShowKeyManagement(false),
    []
  );

  const handleDelete = useCallback(() => {
    if (!organizationId) {
      return;
    }

    void refetchDeletionEligibility().then((result) => {
      if (result.isError || !result.data) {
        showErrorToast("toasts.unexpected.error");
        return;
      }

      const deletionEligibility = result.data;

      if (!deletionEligibility.canDelete) {
        setSLRPromptData({
          labels: {
            title: t("deleteBlocked.title"),
            content: t("deleteBlocked.content", {
              offerCount: deletionEligibility.offerCount,
              bookingRequestCount: deletionEligibility.bookingRequestCount,
              bookingCount: deletionEligibility.bookingCount,
            }),
            cancel: t("deleteBlocked.cancel"),
            confirm: t("deleteBlocked.confirm"),
          },
          onConfirm: hideSLRPrompt,
          onCancel: hideSLRPrompt,
        });
        return;
      }

      setSLRPromptData({
        confirmButtonVariant: "danger",
        labels: t("delete", { returnObjects: true }) as SLRPromptLabels,
        onConfirm: () => {
          deleteOrganization(
            { organizationId },
            {
              onSuccess: () => {
                if (organization?.logoId) {
                  deleteLogo([organization.logoId]);
                }
                navigate(PROFILE_ORGANIZATIONS_PATH);
              },
            }
          );
          hideSLRPrompt();
        },
        onCancel: hideSLRPrompt,
      });
    });
  }, [
    t,
    deleteOrganization,
    organizationId,
    organization?.logoId,
    navigate,
    deleteLogo,
    refetchDeletionEligibility,
  ]);

  const handleLeave = useCallback(() => {
    setSLRPromptData({
      confirmButtonVariant: "danger",
      labels: t("leave", { returnObjects: true }) as SLRPromptLabels,
      onConfirm: () => {
        if (organizationId) {
          leaveOrganization(
            { organizationId },
            {
              onSuccess: () =>
                navigate(PROFILE_ORGANIZATIONS_PATH, { replace: true }),
            }
          );
        }
        hideSLRPrompt();
      },
      onCancel: hideSLRPrompt,
    });
  }, [leaveOrganization, navigate, organizationId, t]);

  const showInvitationList: boolean = useMemo(
    () => hasOrganizationLoaded && isAdmin,
    [hasOrganizationLoaded, isAdmin]
  );

  const handleOrganizationModalClose = useCallback(() => {
    setShowEditModal(false);
    removeQueryParamsFromURL(SHOW_ORGANIZATION_EDIT_PARAM, false);
  }, [removeQueryParamsFromURL]);

  useEffect(() => {
    if (
      ENCRYPTION_ENABLED &&
      isAdmin &&
      showEncryption &&
      !hasKeyManagementModalAutoOpened
    ) {
      setShowKeyManagement(true);
      setHasKeyManagementModalAutoOpened(true);
    }
    if (isAdmin && showOrgEdit) {
      setShowEditModal(true);
    }
  }, [
    hasKeyManagementModalAutoOpened,
    isAdmin,
    setHasKeyManagementModalAutoOpened,
    showEncryption,
    showOrgEdit,
  ]);

  if (isError)
    return (
      <ErrorView
        errorMessage={t("noAccess")}
        showButton
        buttonType="organization"
        buttonPath={PROFILE_ORGANIZATIONS_PATH}
      />
    );

  if (organization)
    return (
      <>
        {SLRPromptData && <SLRPrompt {...SLRPromptData} />}
        {showEditModal && (
          <OrganizationModal
            onClose={handleOrganizationModalClose}
            organization={organization}
          />
        )}
        {ENCRYPTION_ENABLED && showKeyManagement && (
          <KeyManagement
            onOpenEdit={() => setShowEditModal(true)}
            onClose={handleKeyManagementClose}
            organization={organization}
          />
        )}
        <Container fluid className="p-0">
          <OrganizationHeader
            organization={organization}
            isMember={isMember}
            isAdmin={isAdmin}
            onManageKey={() => setShowKeyManagement(true)}
            onEdit={() => setShowEditModal(true)}
            onDelete={handleDelete}
            onLeave={handleLeave}
          />

          <OrganizationDescription
            name={organization?.name}
            detailedDescription={organization?.detailedDescription}
          />
          {showInvitationList && (
            <InvitationsList organizationId={organizationId} />
          )}
          {hasOrganizationLoaded && (
            <MembersList organizationId={organizationId} isAdmin={isAdmin} />
          )}
        </Container>
      </>
    );

  return <SLRSpinner />;
};
